"use client"

import React, { ReactNode, useState, useEffect, useRef } from "react"

import { Button } from "@/components/ui/button"

import { Settings, Home, Users, LayoutList, SettingsIcon } from "lucide-react"
import Link, { LinkProps } from "next/link"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip"

import { cn } from "@/lib/utils"
import { usePathname } from "next/navigation"
import { host } from "lib/api/constants"
import { Logo } from "./layout/Logo"

interface SidebarButtonProps extends LinkProps {
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  children: React.ReactNode
}

const SidebarButton = React.forwardRef<HTMLButtonElement, SidebarButtonProps>(
  ({ href, children, prefetch, buttonProps, ...rest }, ref) => {
    const pathname = usePathname()

    const isActive = pathname?.startsWith(
      new URL(href.toString(), host).pathname
    )
    console.log("ACTIVE?", isActive, pathname, href)

    return (
      <Link href={href} prefetch={prefetch} className="w-full cursor-pointer">
        {/* @ts-ignore */}
        <Button
          ref={ref}
          variant="ghost"
          className={cn(
            "flex w-10 justify-center h-10 text-muted-foreground  p-0 rounded-full",
            isActive ? "text-white bg-wood" : ""
          )}
          {...buttonProps}
          {...rest}
        >
          {children}
        </Button>
      </Link>
    )
  }
)

SidebarButton.displayName = "SidebarButton"

function SidebarContent({ children }: { children: ReactNode }) {
  return (
    <>
      <div>
        <Logo className="h-8" />
      </div>
      <div className="flex flex-col space-y-4 w-full px-2">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <SidebarButton href={`${host}/home`}>
                <Home className="h-5 w-5" />
                <span className="ml-2 block sm:hidden">Home</span>
              </SidebarButton>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={16}
              align="start"
              alignOffset={-10}
            >
              <p>Home</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <SidebarButton href={`${host}/dashboard`}>
                <LayoutList className="h-5 w-5" />
                <span className="ml-2 block sm:hidden">Dashboard</span>
              </SidebarButton>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={16}
              align="start"
              alignOffset={-10}
            >
              <p>Dashboard</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <SidebarButton href={`${host}/subscribers`} prefetch={false}>
                <Users className="h-5 w-5" />
                <span className="ml-2 block sm:hidden">Subscribers</span>
              </SidebarButton>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={16}
              align="start"
              alignOffset={-10}
            >
              <p>Subscribers</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex flex-col space-y-4 w-full px-2">
        {children}

        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <SidebarButton
                href={`${host}/settings/publication`}
                prefetch={false}
              >
                <SettingsIcon className="h-5 w-5" />
                <span className="ml-2 block sm:hidden">
                  Publication settings
                </span>
              </SidebarButton>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={16}
              align="start"
              alignOffset={-10}
            >
              <p>Publication settings</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </>
  )
}

/**
 * Full-page, app-wide, universal sidebar.
 */
function Sidebar({ children }: { children?: ReactNode }) {
  const pathname = usePathname()
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const navbarRef = useRef<HTMLDivElement>(null)

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Show navbar at the top of the page or when scrolling up
      if (currentScrollY <= 20 || currentScrollY < lastScrollY) {
        setIsVisible(true)
      }
      // Hide navbar when scrolling down past a threshold
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [lastScrollY])

  // Don't render the sidebar if we're in embed mode
  if (pathname?.includes("/embed")) {
    return null
  }

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden sm:flex w-14 h-screen flex-col items-center justify-between py-4 transition-all duration-300 border-r bg-background fixed z-50">
        <SidebarContent>{children}</SidebarContent>
      </div>

      {/* Mobile bottom navbar with scroll animation */}
      <div
        ref={navbarRef}
        className={cn(
          "sm:hidden fixed bottom-0 left-0 right-0 z-50 h-16 bg-background border-t border-linen dark:border-wood flex items-center justify-around px-4 transition-transform duration-300",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
      >
        <Link
          href={`${host}/home`}
          className="flex flex-col items-center space-y-1"
        >
          <Home className="h-5 w-5  text-foreground" />
          <span className="text-xs text-foreground">Home</span>
        </Link>

        <Link
          href={`${host}/dashboard`}
          className="flex flex-col items-center space-y-1"
        >
          <LayoutList className="h-5 w-5 text-foreground" />
          <span className="text-xs text-foreground">Dashboard</span>
        </Link>

        <Link
          href={`${host}/subscribers`}
          className="flex flex-col items-center space-y-1"
        >
          <Users className="h-5 w-5 text-foreground" />
          <span className="text-xs text-foreground">Subscribers</span>
        </Link>

        <Link
          href={`${host}/settings/publication`}
          className="flex flex-col items-center space-y-1"
        >
          <Settings className="h-5 w-5 text-foreground" />
          <span className="text-xs text-foreground">Settings</span>
        </Link>

        {children}
      </div>
    </>
  )
}

export default Sidebar
